import React, { useState, useEffect, useCallback } from 'react';
import { useLiveUpdates } from '../hooks/useLiveUpdates';
import { 
  ClockIcon, 
  ExclamationTriangleIcon, 
  CheckCircleIcon, 
  MapPinIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  EyeIcon,
  ArrowPathIcon,
  CalendarIcon,
  UserGroupIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline';
import { apiService } from '../services/apiService';
import { gisService } from '../services/gisService';
import HeatmapVisualization from '../components/HeatmapVisualization';
import ClusterVisualization from '../components/ClusterVisualization';
import exportService from '../services/exportService';
import { EXPORT_FORMATS } from '../config/dashboardConfig';
import PageHeader from '../components/ui/PageHeader';
import StatCard from '../components/ui/StatCard';
import Badge, { BadgeTone } from '../components/ui/Badge';
import Button from '../components/ui/Button';

const PRIORITY_TONE: Record<string, BadgeTone> = {
  critical: 'danger',
  high: 'warning',
  medium: 'warning',
  low: 'success',
};

const SEVERITY_TONE: Record<string, BadgeTone> = {
  high: 'danger',
  medium: 'warning',
  low: 'success',
};

const ACTIVITY_STATUS_TONE: Record<string, BadgeTone> = {
  completed: 'success',
  in_progress: 'info',
  assigned: 'warning',
  pending: 'neutral',
};

export interface DashboardMetrics {
  firstResponseTime: {
    average: number;
    target: number;
    trend: 'up' | 'down' | 'stable';
    percentage: number;
  };
  agingTickets: {
    total: number;
    critical: number;
    high: number;
    medium: number;
    low: number;
    distribution: Array<{ age: string; count: number; percentage: number }>;
  };
  resolutionRates: {
    overall: number;
    byDepartment: Array<{ department: string; rate: number; count: number }>;
    byCategory: Array<{ category: string; rate: number; count: number }>;
    trend: Array<{ date: string; rate: number }>;
  };
  hotspots: {
    total: number;
    critical: number;
    byWard: Array<{ ward: string; count: number; severity: string }>;
    byAsset: Array<{ asset: string; count: number; type: string }>;
    recurrentFaults: Array<{ location: string; count: number; lastOccurrence: string }>;
  };
}

export interface LiveData {
  totalReports: number;
  activeAssignments: number;
  onlineTechnicians: number;
  criticalIssues: number;
  recentActivity: Array<{
    id: string;
    type: 'report' | 'assignment' | 'escalation';
    title: string;
    status: string;
    priority: string;
    timestamp: string;
    location: string;
    category?: string;
  }>;
}

const LiveDashboard: React.FC = () => {
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [liveData, setLiveData] = useState<LiveData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [selectedTimeframe, setSelectedTimeframe] = useState<'24h' | '7d' | '30d' | '90d'>('7d');
  const [selectedDepartment, setSelectedDepartment] = useState<string>('all');
  const [departments, setDepartments] = useState<any[]>([]);
  const [isExporting, setIsExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState<'excel' | 'pdf' | 'csv' | 'json'>('excel');

  // Load initial data
  useEffect(() => {
    loadDashboardData();
    loadDepartments();

    // Fallback polling only (5 min) — real-time updates arrive over WebSocket.
    const interval = setInterval(() => {
      if (autoRefresh) {
        loadDashboardData();
      }
    }, 300000);

    return () => clearInterval(interval);
  }, [autoRefresh, selectedTimeframe, selectedDepartment]);

  // Live by default: refresh on server push instead of polling.
  useLiveUpdates((m) => {
    if (m.type === 'dashboard_update' || m.type === 'alert') {
      loadDashboardData();
    }
  });

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      setError('');

      // Load all dashboard data in parallel
      const [metricsData, liveDataResult] = await Promise.all([
        loadMetrics(),
        loadLiveData(),
      ]);

      setMetrics(metricsData);
      setLiveData(liveDataResult);
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error('Error loading dashboard data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadMetrics = async (): Promise<DashboardMetrics> => {
    try {
      const response = await apiService.get(`/dashboard/metrics?timeframe=${selectedTimeframe}&department=${selectedDepartment}`);
      if (response.success) {
        return response.data;
      }
      throw new Error('Failed to load metrics');
    } catch (err) {
      console.error('Error loading metrics:', err);
      setError('Failed to load dashboard metrics');
      throw err;
    }
  };

  const loadLiveData = async (): Promise<LiveData> => {
    try {
      const response = await apiService.get('/dashboard/live-data');
      if (response.success) {
        return response.data;
      }
      throw new Error('Failed to load live data');
    } catch (err) {
      console.error('Error loading live data:', err);
      setError('Failed to load live data');
      throw err;
    }
  };

  const loadDepartments = async () => {
    try {
      const response = await apiService.get('/departments');
      if (response.success) {
        setDepartments(response.data);
      }
    } catch (err) {
      console.error('Error loading departments:', err);
    }
  };

  const handleExport = async () => {
    if (!metrics || !liveData) return;
    
    setIsExporting(true);
    try {
      await exportService.exportDashboard(metrics, liveData, {
        format: exportFormat,
        timeframe: selectedTimeframe,
        department: selectedDepartment,
        includeCharts: true,
        includeRawData: true,
      });
    } catch (error) {
      console.error('Export failed:', error);
      setError('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const generateMockMetrics = (): DashboardMetrics => {
    return {
      firstResponseTime: {
        average: 23.5,
        target: 30,
        trend: 'down',
        percentage: 78.3,
      },
      agingTickets: {
        total: 156,
        critical: 12,
        high: 34,
        medium: 67,
        low: 43,
        distribution: [
          { age: '0-24h', count: 89, percentage: 57.1 },
          { age: '24-48h', count: 45, percentage: 28.8 },
          { age: '48-72h', count: 15, percentage: 9.6 },
          { age: '72h+', count: 7, percentage: 4.5 },
        ],
      },
      resolutionRates: {
        overall: 87.3,
        byDepartment: [
          { department: 'Electricity', rate: 92.1, count: 45 },
          { department: 'Water', rate: 89.5, count: 38 },
          { department: 'Roads', rate: 84.2, count: 52 },
          { department: 'Waste', rate: 91.8, count: 21 },
        ],
        byCategory: [
          { category: 'Power Outage', rate: 95.2, count: 28 },
          { category: 'Water Leak', rate: 88.7, count: 31 },
          { category: 'Pothole', rate: 82.1, count: 45 },
          { category: 'Garbage Collection', rate: 93.4, count: 18 },
        ],
        trend: Array.from({ length: 30 }, (_, i) => ({
          date: new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          rate: 85 + Math.random() * 10,
        })),
      },
      hotspots: {
        total: 23,
        critical: 5,
        byWard: [
          { ward: 'Ward 1', count: 8, severity: 'high' },
          { ward: 'Ward 2', count: 6, severity: 'medium' },
          { ward: 'Ward 3', count: 5, severity: 'high' },
          { ward: 'Ward 4', count: 4, severity: 'low' },
        ],
        byAsset: [
          { asset: 'Substation A', count: 12, type: 'electricity' },
          { asset: 'Water Main B', count: 8, type: 'water' },
          { asset: 'Road Section C', count: 15, type: 'roads' },
          { asset: 'Waste Facility D', count: 3, type: 'waste' },
        ],
        recurrentFaults: [
          { location: 'Main Street Junction', count: 7, lastOccurrence: '2024-01-15' },
          { location: 'Industrial Park', count: 5, lastOccurrence: '2024-01-14' },
          { location: 'Residential Area', count: 4, lastOccurrence: '2024-01-13' },
        ],
      },
    };
  };

  const generateMockLiveData = (): LiveData => {
    return {
      totalReports: 1247,
      activeAssignments: 89,
      onlineTechnicians: 23,
      criticalIssues: 7,
      recentActivity: [
        {
          id: '1',
          type: 'report',
          title: 'Power outage in Ward 1',
          status: 'assigned',
          priority: 'critical',
          timestamp: new Date().toISOString(),
          location: 'Ward 1, Main Street',
        },
        {
          id: '2',
          type: 'assignment',
          title: 'Water leak repair',
          status: 'in_progress',
          priority: 'high',
          timestamp: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
          location: 'Ward 2, Oak Avenue',
        },
        {
          id: '3',
          type: 'escalation',
          title: 'Road closure required',
          status: 'pending',
          priority: 'medium',
          timestamp: new Date(Date.now() - 60 * 60 * 1000).toISOString(),
          location: 'Ward 3, Industrial Road',
        },
      ],
    };
  };

  const getTrendIcon = (trend: 'up' | 'down' | 'stable') => {
    switch (trend) {
      case 'up':
        return <ArrowTrendingUpIcon className="h-5 w-5 text-red-500" />;
      case 'down':
        return <ArrowTrendingDownIcon className="h-5 w-5 text-green-500" />;
      default:
        return <ChartBarIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />;
    }
  };

  if (isLoading && !metrics) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error && !metrics) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error loading dashboard</h3>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Live Dashboard"
        subtitle="Real-time monitoring of municipal service operations"
        actions={
          <>
            <select
              value={selectedTimeframe}
              onChange={(e) => setSelectedTimeframe(e.target.value as any)}
              className="rounded-sm border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="24h">Last 24 Hours</option>
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
              <option value="90d">Last 90 Days</option>
            </select>
            <select
              value={selectedDepartment}
              onChange={(e) => setSelectedDepartment(e.target.value)}
              className="rounded-sm border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="all">All Departments</option>
              {departments.map(dept => (
                <option key={dept.id} value={dept.id}>{dept.name}</option>
              ))}
            </select>
            <Button variant="secondary" icon={ArrowPathIcon} onClick={loadDashboardData}>
              Refresh
            </Button>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="mr-2"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Auto-refresh</span>
            </label>

            <select
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value as any)}
              className="rounded-sm border border-gray-300 px-3 py-2 text-sm"
            >
              {EXPORT_FORMATS.map(format => (
                <option key={format.value} value={format.value}>
                  {format.icon} {format.label}
                </option>
              ))}
            </select>
            <Button variant="secondary" disabled={isExporting || !metrics || !liveData} onClick={handleExport}>
              {isExporting ? 'Exporting…' : 'Export'}
            </Button>
          </>
        }
      />

      {/* Live Status Cards */}
      {liveData && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Total Reports" value={liveData.totalReports.toLocaleString()} icon={ExclamationTriangleIcon} tone="blue" />
          <StatCard label="Active Assignments" value={liveData.activeAssignments} icon={ClockIcon} tone="amber" />
          <StatCard label="Online Technicians" value={liveData.onlineTechnicians} icon={UserGroupIcon} tone="emerald" />
          <StatCard label="Critical Issues" value={liveData.criticalIssues} icon={ExclamationTriangleIcon} tone="red" />
        </div>
      )}

      {metrics && (
        <>
          {/* First Response Time */}
          <div className="rounded-sm border border-gray-300 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">First Response Time</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-primary-700">{metrics.firstResponseTime.average.toFixed(1)}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Average Minutes</div>
                <div className="flex items-center justify-center mt-2">
                  {getTrendIcon(metrics.firstResponseTime.trend)}
                  <span className="ml-1 text-sm text-gray-600 dark:text-gray-400">
                    {metrics.firstResponseTime.trend === 'down' ? 'Improving' : 
                     metrics.firstResponseTime.trend === 'up' ? 'Declining' : 'Stable'}
                  </span>
                </div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600">{metrics.firstResponseTime.target}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Target Minutes</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-accent-700">{metrics.firstResponseTime.percentage.toFixed(1)}%</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Target Achievement</div>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                  <div
                    className="bg-accent-500 h-2 rounded-full"
                    style={{ width: `${Math.min(100, metrics.firstResponseTime.percentage)}%` }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Aging Tickets */}
          <div className="rounded-sm border border-gray-300 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Aging Tickets</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-red-50 rounded-lg">
                    <div className="text-2xl font-bold text-red-600">{metrics.agingTickets.critical}</div>
                    <div className="text-sm text-red-600">Critical</div>
                  </div>
                  <div className="text-center p-4 bg-orange-50 rounded-lg">
                    <div className="text-2xl font-bold text-orange-600">{metrics.agingTickets.high}</div>
                    <div className="text-sm text-orange-600">High</div>
                  </div>
                  <div className="text-center p-4 bg-yellow-50 rounded-lg">
                    <div className="text-2xl font-bold text-yellow-600">{metrics.agingTickets.medium}</div>
                    <div className="text-sm text-yellow-600">Medium</div>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">{metrics.agingTickets.low}</div>
                    <div className="text-sm text-green-600">Low</div>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Age Distribution</h4>
                <div className="space-y-2">
                  {metrics.agingTickets.distribution.map((item, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">{item.age}</span>
                      <div className="flex items-center space-x-2">
                        <div className="w-20 bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-primary-600 h-2 rounded-full"
                            style={{ width: `${item.percentage}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{item.count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Resolution Rates */}
          <div className="rounded-sm border border-gray-300 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Resolution Rates</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <div className="text-center mb-4">
                  <div className="text-4xl font-bold text-green-600">{metrics.resolutionRates.overall.toFixed(1)}%</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Overall Resolution Rate</div>
                </div>
                <div className="space-y-3">
                  {metrics.resolutionRates.byDepartment.map((dept, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="font-medium text-gray-900 dark:text-gray-100">{dept.department}</span>
                      <div className="flex items-center space-x-2">
                        <div className="w-16 bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-green-600 h-2 rounded-full"
                            style={{ width: `${dept.rate}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{dept.rate.toFixed(1)}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">By Category</h4>
                <div className="space-y-3">
                  {metrics.resolutionRates.byCategory.map((cat, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="font-medium text-gray-900 dark:text-gray-100">{cat.category}</span>
                      <div className="flex items-center space-x-2">
                        <div className="w-16 bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-primary-600 h-2 rounded-full"
                            style={{ width: `${cat.rate}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{cat.rate.toFixed(1)}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Hotspots */}
          <div className="rounded-sm border border-gray-300 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Hotspots & Recurrent Faults</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="text-center p-4 bg-red-50 rounded-lg">
                    <div className="text-2xl font-bold text-red-600">{metrics.hotspots.total}</div>
                    <div className="text-sm text-red-600">Total Hotspots</div>
                  </div>
                  <div className="text-center p-4 bg-orange-50 rounded-lg">
                    <div className="text-2xl font-bold text-orange-600">{metrics.hotspots.critical}</div>
                    <div className="text-sm text-orange-600">Critical</div>
                  </div>
                </div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">By Ward</h4>
                <div className="space-y-2">
                  {metrics.hotspots.byWard.map((ward, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <span className="font-medium text-gray-900 dark:text-gray-100">{ward.ward}</span>
                      <div className="flex items-center space-x-2">
                        <Badge tone={SEVERITY_TONE[ward.severity] ?? 'neutral'}>{ward.severity}</Badge>
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{ward.count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Recurrent Faults</h4>
                <div className="space-y-3">
                  {metrics.hotspots.recurrentFaults.map((fault, index) => (
                    <div key={index} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-900 dark:text-gray-100">{fault.location}</span>
                        <span className="text-sm text-gray-500 dark:text-gray-400">{fault.count} occurrences</span>
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        Last: {new Date(fault.lastOccurrence).toLocaleDateString()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Recent Activity */}
      {liveData && (
        <div className="rounded-sm border border-gray-300 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Recent Activity</h2>
          <div className="space-y-3">
            {liveData.recentActivity.map((activity) => (
              <div key={activity.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-lg ${
                    activity.type === 'report' ? 'bg-primary-100 text-primary-700' :
                    activity.type === 'assignment' ? 'bg-green-100 text-green-600' :
                    'bg-orange-100 text-orange-600'
                  }`}>
                    {activity.type === 'report' && <ExclamationTriangleIcon className="h-4 w-4" />}
                    {activity.type === 'assignment' && <CheckCircleIcon className="h-4 w-4" />}
                    {activity.type === 'escalation' && <ArrowPathIcon className="h-4 w-4" />}
                  </div>
                  <div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">{activity.title}</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">{activity.location}</div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Badge tone={PRIORITY_TONE[activity.priority.toLowerCase()] ?? 'neutral'}>{activity.priority}</Badge>
                  <Badge tone={ACTIVITY_STATUS_TONE[activity.status.toLowerCase()] ?? 'neutral'}>
                    {activity.status.replace('_', ' ')}
                  </Badge>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {new Date(activity.timestamp).toLocaleTimeString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveDashboard;
